import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {Card, Gif, MEDIA_QUALITY} from './Gif';
import {GifOverlay} from './GifCommon';
import {PostMathService} from '../services/mathService';
import {CustomComponent, Link} from '../../common/components';
import {cleanMultiwordTag, getCanonicalPostId, getGifEventParams, gifCountryAllowed, isStaticImage, isSticker, window} from '../../common/util';
import emitter from '../../common/util/emitter';
import {GifFavButton} from '../components/GifFavButton';
import {ShareCount} from '../components/ShareCount';
import type {MathService} from '../services/mathService';

import './UniversalGifList.scss';
export type Slice = {
    gif: api.Post;
    index: number;
};


export function searchEntrypointCardData(context) {
    const gettextSub = context.gtService.gettextSub;
    const localizeUrlPath = context.gtService.localizeUrlPath;
    const url = localizeUrlPath('/gif-maker?utm_source=search-page&utm_medium=internal&utm_campaign=gif-maker-entrypoints');

    return {
        isCard: true,
        itemurl: url,
        tags: [],
        media_formats: {
            tinygif: {
                url: `/assets/img/gif-maker-entrypoints/search-entrypoint-optimized.gif`,
                dims: [516, 715],
                size: 125566,
            },
            tinywebp: {
                url: `/assets/img/gif-maker-entrypoints/search-entrypoint-optimized.webp`,
                dims: [516, 715],
                size: 104724,
            },
            tinymp4: {
                url: `/assets/img/gif-maker-entrypoints/search-entrypoint-optimized.mp4`,
                dims: [516, 715],
                size: 35060,
            },
        },
        altDims: [516, 420],
        header: <div className="header">
            <p>{gettextSub('Click here')}</p>
            <p>{gettextSub('to upload to Tenor')}</p>
        </div>,
        footer: <div className="footer">
            <p>{gettextSub('Upload your own GIFs')}</p>
        </div>,
    };
}

export function makeSlice(
    ary: api.Post[], start: number, num: number): Slice[] {
    const slice = [];
    let max = start + num;
    if (max > ary.length) {
        max = ary.length;
    }

    for (let index = start; index < max; index++) {
        slice.push({
            'index': index,
            'gif': ary[index],
        });
    }
    return slice;
}


export class UniversalGifListItem extends CustomComponent {
    static debugNamespace = 'UniversalGifListItem';

    @autobind
    reuseGifData() {
        // When a GIF is selected, we want to keep its source_id, so we need to
        // avoid refetching it from the API.
        // If the same GIF shows up in another stream, the old cache data
        // should get overwritten here if it's clicked.
        const val = {
            loaded: true,
            pending: false,
            results: [this.props.gif],
            promise: null,
        };
        val.promise = Promise.resolve(val);
        const id = getCanonicalPostId(this.props.gif);
        this.context.store.set('api.gifs.byId.*', [id], val);
    }
    @autobind
    handleClick() {
        this.reuseGifData();
        const params = getGifEventParams(
            this.context, this.props.index, this.props.gif);
        this.context.apiService.registerEvent(
            'giflistitem_result_tap', params, true);
    }
    makeTagAnchors(tags: string[]) {
        const ary = [];
        const maxLen = 4;
        let len = tags.length;
        const maxChars = 30;
        let numChars = 0;
        let tag;
        if (len > maxLen) {
            len = maxLen;
        }
        for (let i = 0; i < len; i++) {
            numChars += tags[i].length;
            if (numChars > maxChars) {
                break;
            }
            tag = cleanMultiwordTag(tags[i]);
            if (tag) {
                ary.push(<li><Link
                    to={this.linkToSearch(tag)}
                >{`#${tag.replace(/ /g, '-')}`}</Link></li>);
            }
        }
        return ary;
    }
    render() {
        const gif = this.props.gif;
        const showGifFaveButton = !isSticker(gif) &&
            !isStaticImage(gif) &&
            gif.id &&
            !gif.unprocessed;
        const dims = Gif.getFormatDims(gif, 'tinygif') ||
            [undefined, undefined];
        const clickable = !(gif.unprocessed ||
            gif.policy_status === 'PENDING_INITIAL_REVIEW');
        const url = gif.isCard ?
            gif.itemurl :
            gif.itemurl ? this.linkToView(gif) : undefined;

        return (
            <figure
                className={`UniversalGifListItem${clickable ? ' clickable' : ''}`}
                data-index={this.props.index}
                data-col-index={gif.details.colIndex}
                data-width={dims[0]}
                data-height={dims[1]}
                style={{top: `${gif.details.y}px`}}
            >
                <Link to={url} onClick={this.handleClick}>
                    {gif.isCard ? <Card
                        gif={gif}
                        width={gif.details.width}
                        height={gif.details.height}
                    /> : <Gif
                        inList={true}
                        inUniversalSearch={true}
                        gif={gif}
                        width={gif.details.width}
                        height={gif.details.height}
                        quality={MEDIA_QUALITY.TINYWEBP}
                    />}
                    {gif.ad_badge_info?.badges.length && <img
                        className={
                            `badge pos-${gif.ad_badge_info.badges[0].position}`}
                        src={gif.ad_badge_info.badges[0].url}
                        width={gif.ad_badge_info.badges[0].dims[0] / 2}
                        height={gif.ad_badge_info.badges[0].dims[1] / 2}
                    />}
                    <div className="overlay" />
                </Link>
                { showGifFaveButton &&
                    <div className="actions">
                        <GifFavButton gif={gif} />
                    </div>
                }
                <figcaption className="tags">
                    <ul>
                        {this.makeTagAnchors(gif.tags)}
                    </ul>
                </figcaption>
                <GifOverlay gif={gif}/>
                {this.props.showShareCount && <ShareCount gif={gif}/>}
            </figure>
        );
    }
}


export class UniversalGifList extends CustomComponent {
    static debugNamespace = 'UniversalGifList';

    element: null|HTMLElement;
    extra: number;
    layout: MathService<any>;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);

        this.element = null;
        this.setLayout();
        this.extra = 3;

        this.state = {
            ...this.state,
            start: 0,
            num: 0,
            emptyQuery: false,
            gifColumns: [],
        };

        this.addCardsIfNeeded(this.props);

        this.setEmptyQuery(this.props, this.state);

        this.setGifListSliceIfNeeded(this.props, this.state);
    }
    componentWillUpdate(nextProps: jsx.Props, nextState: jsx.State) {
        let newLayout = false;
        if (this.props.staticColumns !== nextProps.staticColumns) {
            this.setLayout(nextProps);
            newLayout = true;
        }
        this.addCardsIfNeeded(nextProps);
        if (newLayout || this.props.processedGifs != nextProps.processedGifs) {
            this.layout.compute(nextProps.processedGifs, true);
            this.setGifListSliceIfNeeded(nextProps, nextState);
        }

        if (this.props.query != nextProps.query) {
            this.setEmptyQuery(nextProps, nextState);
        }
    }
    componentDidMount() {
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);
    }
    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
    }
    @autobind
    handleScroll() {
        this.setGifListSliceIfNeeded(this.props, this.state);
    }
    @autobind
    handleResize() {
        this.layout.compute(this.props.processedGifs, true);
        this.setGifListSliceIfNeeded(this.props, this.state);
    }
    setLayout(props?: jsx.Props) {
        props = props || this.props;
        this.layout = new PostMathService({
            element: this.element,
            staticColumns: props.staticColumns,
            showShareCount: props.showShareCount,
            isUniversalGifList: true,
        });
    }
    @autobind
    setElement(element: HTMLElement|null) {
        this.element = element;
        this.setLayout();
        this.setGifListSliceIfNeeded(this.props, this.state);
    }
    addCardsIfNeeded(props: jsx.Props) {
        if (!props.processedGifs) {
            // we don't want to edit the original list in place.
            props.processedGifs = props.gifs.filter((gif: api.Post) => {
                return gifCountryAllowed(gif, this.context);
            });
            if (props.addGifMakerEntrypoint &&
                props.processedGifs.length >= 16) {
                props.processedGifs.splice(
                    16, 0, searchEntrypointCardData(this.context));
            }
        }
    }
    setEmptyQuery(props: jsx.Props, state: jsx.State) {
        const query = 'query' in props ? props.query : null;
        if (query) {
            state.emptyQuery = !query.length;
        }
    }
    @autobind
    setGifListSliceIfNeeded(props: jsx.Props, state: jsx.State) {
        // TODO: might be best to always call layout.compute here, using a
        // flag for if this.props.gifs has been updated.
        const oldNum = state.num;
        const oldStart = state.start;
        let start = oldStart;
        let num = oldNum;

        if (process.env.BROWSER) {
            const startAndNum = this.updateStartAndNum(props, state);
            if (startAndNum) {
                start = startAndNum[0];
                num = startAndNum[1];
            }
        } else {
            start = 0;
            // Show everything we have
            num = props.processedGifs.length;
        }

        if (start === oldStart && num === oldNum) {
            // nothing's changed
            return;
        }
        this.setGifListSlice(start, num);
    }
    setGifListSlice(start: number, num: number) {
        // The code breaks if these values aren't set on state synchronously.
        // When adding logging to the render() method setting asynchronously,
        // it seems render() last gets called with old values for start and
        // num, but I (@sth) can't find where that could be coming from. It
        // could be a bug in Inferno.
        // FIXME: we shouldn't be doing this, better to replce with async
        // setState() calls.
        Object.assign(this.state, {start, num});
        this.triggerUpdate();
    }
    setGifList() {
        if (this.props.processedGifs?.length) {
            this.layout.compute(this.props.processedGifs);
            this.setGifListSliceIfNeeded(this.props, this.state);
        }
    }
    updateStartAndNum(props: jsx.Props, state: jsx.State) {
        if (!this.element) {
            return;
        }
        const boundingRect = this.element.getBoundingClientRect();
        const elementTop = boundingRect.top + window.pageYOffset;
        const viewportTop = window.pageYOffset;
        const viewportBottom = viewportTop + window.innerHeight;
        const gifList = props.processedGifs;
        let start = 0;
        let num = state.num;

        if (!gifList) {
            return;
        }

        if (gifList.length && gifList[0].details) {
            let i = 0;
            for (; i < gifList.length; i++) {
                // If the bottom of this element is above the top of the
                // viewport.
                if (gifList[i].details.y + gifList[i].details.height +
                    elementTop > viewportTop) {
                    start = i;
                    break;
                }
            }
            num = gifList.length - start;
            for (; i < gifList.length; i++) {
                // If the top of this element is below the bottom of the
                // viewport.
                if (gifList[i].details.y + elementTop > viewportBottom) {
                    num = i - start;
                    break;
                }
            }
        }

        const computedLayout = this.layout.compute(this.props.processedGifs);
        start -= computedLayout.numColumns;
        num += computedLayout.numColumns;

        // Render a few extra elements, but only after the first few
        // have had a chance to load.
        num += 2 * this.extra;
        start -= this.extra;
        if (start < 0) {
            // Set start to 0, but keep ending index the same.
            num += start;
            start = 0;
        }
        if (start + num >= (gifList.length - 10)) {
            // Trigger event when we're close to end of list.
            emitter.emit('list-items-exhausted');
            this.props.itemsExhaustedCallback?.();
        }
        return [start, num];
    }
    render() {
        const gettextSub = this.context.gtService.gettextSub;

        // May need to call this.layout.compute in case this.props.gifs
        // gets updated in place and new GIFs don't have details. This function
        // will continue where it left off.
        const computedLayout = this.layout.compute(this.props.processedGifs);

        let gifListSlice: Slice[] = [];
        const gifColumns: Slice[][] = [];

        if (process.env.BROWSER) {
            gifListSlice = makeSlice(
                this.props.processedGifs, this.state.start, this.state.num);
        } else {
            gifListSlice = makeSlice(
                this.props.processedGifs, 0, this.props.gifs.length);
        }

        for (let i = gifColumns.length;
            i < computedLayout.numColumns; i++) {
            gifColumns.push([] as Slice[]);
        }

        for (const item of gifListSlice) {
            gifColumns[item.gif.details.column].push(item);
        }

        const gifColumnsElements = [];
        let gifColumn;
        for (let gifColumnIdx = 0;
            gifColumnIdx < gifColumns.length; gifColumnIdx++) {
            gifColumn = gifColumns[gifColumnIdx];

            // NOTE: `hasKeyedChildren` is Inferno specific, but `key` is not.
            gifColumnsElements.push(<div
                className={'column'}
                $HasKeyedChildren={true}
            >{ gifColumn.map((item: Slice) => {
                    if (item === null) {
                        return <div/>;
                    }
                    return <UniversalGifListItem
                        gif={item.gif}
                        index={item.gif.details.index}
                        key={item.gif.details.index}
                        showShareCount={this.props.showShareCount}
                    />;
                }) }</div>);
        }

        const loaded = this.props.loaded;
        const noResults = (loaded === undefined || loaded) &&
            !this.state.emptyQuery &&
            this.props.processedGifs.length === 0;
        const style: app.Style = {};
        if (!noResults) {
            style.height = `${this.props.processedGifs.length ? computedLayout.loaderHeight : 400}px`;
        }

        return <div
            ref={this.setElement}
            className={`UniversalGifList`}
            data-columns={this.props.staticColumns}
            style={style}
        >
            { computedLayout.loaderHeight > 0 && gifColumnsElements}
            { this.props.pending && <div className="spinner"></div> }
            { noResults && <p className="no-results">
                {gettextSub('No Results')}
            </p> }
            { !process.env.BROWSER && <script
                type="text/javascript"
                nonce={this.context.response.locals.cspNonce}
                dangerouslySetInnerHTML={{__html:
                    this.context.PRIVATE_CONSTANTS.INLINE_MATH_SERVICE_SRC || ''}}
            ></script>}
        </div>;
    }
}
